import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Typography, 
  Box, 
  Paper,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import { Clear as ClearIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { get, getTimelineConfig, setPlanningQuarter, getPlanningItems, authPost, getTeamsWithStreams } from '../../services/apiService';
import { formatDate } from '../../utils/formatters';
import WorkItemsFilterPanel from './WorkItemsFilterPanel';
import Loading from '../common/Loading';
import WorkItemsTreeView from './WorkItemsTreeView';
import OrphanItemsTable from './OrphanItemsTable';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

/**
 * WorkItemsView component for displaying a hierarchical view of work items
 * @param {Object} props
 * @param {string} [props.streamName] - When set, filter to epics in this WSR stream (e.g. "Core", "UnMapped"). Omit for no stream filter.
 * @param {string} [props.initialPlanningAdoId] - Optional ADO ID from URL for planning deep link
 * @returns {JSX.Element} WorkItemsView component
 */
const WorkItemsView = ({ streamName = null, initialPlanningAdoId } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workItems, setWorkItems] = useState([]);
  const [filteredWorkItems, setFilteredWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [matchedItemIds, setMatchedItemIds] = useState(new Set());
  const [timelineConfig, setTimelineConfig] = useState(null);
  const [orphanItems, setOrphanItems] = useState([]);
  const [mainTab, setMainTab] = useState(0); // 0 = Work Items, 1 = Orphaned Items
  const [plannedItemIds, setPlannedItemIds] = useState(new Set()); // ADO IDs that are in Planning (for row highlight)
  const [refreshing, setRefreshing] = useState(false);
  const [teamsWithStreams, setTeamsWithStreams] = useState([]);

  // Load timeline config
  useEffect(() => {
    const loadTimelineConfig = async () => {
      try {
        const config = await getTimelineConfig();
        setTimelineConfig(config);
      } catch (err) {
        console.error('Error loading timeline config:', err);
      }
    };
    loadTimelineConfig();
    
    // Listen for timeline config updates
    const handleTimelineConfigUpdate = () => {
      loadTimelineConfig();
    };
    window.addEventListener('timelineConfigUpdated', handleTimelineConfigUpdate);
    return () => window.removeEventListener('timelineConfigUpdated', handleTimelineConfigUpdate);
  }, []);

  // Load teams with streams when filtering by stream (for streamName prop)
  useEffect(() => {
    if (!streamName) return;
    const loadTeams = async () => {
      try {
        const teams = await getTeamsWithStreams();
        setTeamsWithStreams(Array.isArray(teams) ? teams : []);
      } catch (err) {
        console.error('Error loading teams with streams:', err);
        setTeamsWithStreams([]);
      }
    };
    loadTeams();
  }, [streamName]);
  
  // Check for ADO ID in URL params and set search filter
  useEffect(() => {
    const adoIdParam = searchParams.get('adoId');
    if (adoIdParam) {
      setFilters(prev => ({
        ...prev,
        searchText: adoIdParam
      }));
      // Clear the URL param after setting filter
      searchParams.delete('adoId');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Define quarters based on timeline configuration
  const quarters = useMemo(() => {
    if (!timelineConfig) {
      // Default quarters if timeline config not loaded yet
      const now = new Date();
      const baseYear = now.getFullYear();
      const list = [];
      for (let year = baseYear; year <= baseYear + 1; year++) {
        for (let q = 1; q <= 4; q++) {
          list.push({
            year,
            quarter: q,
            label: `${year}-Q${q}`,
            startDate: new Date(year, (q - 1) * 3, 1),
            endDate: new Date(year, q * 3, 0)
          });
        }
      }
      list.push({ year: null, quarter: null, label: 'To be Planned', startDate: null, endDate: null });
      return list;
    }

    // Generate quarters based on timeline config
    const quartersList = [];
    const startYear = timelineConfig.startYear;
    const endYear = timelineConfig.endYear;
    const startQuarter = timelineConfig.startQuarter;
    const endQuarter = timelineConfig.endQuarter;

    // Generate quarters from start to end
    for (let year = startYear; year <= endYear; year++) {
      const startQ = year === startYear ? startQuarter : 1;
      const endQ = year === endYear ? endQuarter : 4;
      
      for (let q = startQ; q <= endQ; q++) {
        // Calculate quarter dates in UTC to match database dates
        const startMonth = (q - 1) * 3; // 0, 3, 6, 9 for Q1-Q4
        const startDate = new Date(Date.UTC(year, startMonth, 1, 0, 0, 0, 0));
        
        // Quarter ends: last day of last month at 23:59:59.999 UTC
        const endMonth = q * 3 - 1; // 2, 5, 8, 11 for Q1-Q4
        const endDate = new Date(Date.UTC(year, endMonth + 1, 0, 23, 59, 59, 999));
        
        quartersList.push({
          year,
          quarter: q,
          label: `${year}-Q${q}`,
          startDate,
          endDate
        });
      }
    }

    // Add To be Planned at the end (TBD superseded by To be Planned)
    quartersList.push({ year: null, quarter: null, label: 'To be Planned', startDate: null, endDate: null });
    return quartersList;
  }, [timelineConfig]);

  // Team → stream map (WSR): used when streamName is set to filter epics by stream
  const teamToStreamMap = useMemo(() => {
    const map = new Map();
    teamsWithStreams.forEach(team => {
      if (team.id) map.set(team.id, team.stream || 'UnMapped');
      if (team.displayName) map.set(team.displayName, team.stream || 'UnMapped');
    });
    return map;
  }, [teamsWithStreams]);

  const getStreamForTeam = useCallback((team) => {
    if (!team || team === 'N/A' || String(team).trim() === '') return 'UnMapped';
    return teamToStreamMap.get(team) || 'UnMapped';
  }, [teamToStreamMap]);

  // When streamName is set, filter epics to that stream only; otherwise use all work items
  const workItemsByStream = useMemo(() => {
    if (!streamName) return workItems;
    return workItems.filter(epic => getStreamForTeam(epic.team) === streamName);
  }, [workItems, streamName, getStreamForTeam]);
  
  // Auto-select current quarter on data/quarters load
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;
    
    // Find the index of the current quarter
    const currentQuarterIndex = quarters.findIndex(q => 
      q.year === currentYear && q.quarter === currentQuarter
    );
    
    if (currentQuarterIndex !== -1) {
      setActiveTab(currentQuarterIndex);
    } else {
      // If current quarter not found, default to first quarter
      setActiveTab(0);
    }
  }, [quarters]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Get the selected quarter
  const getSelectedQuarter = (quarterIndex) => {
    if (quarterIndex === null || quarterIndex < 0 || quarterIndex >= quarters.length) {
      return null;
    }
    return quarters[quarterIndex];
  };

  // Helper function to check if an item matches the search text (ADO ID only)
  const matchesSearch = useCallback((item, searchText) => {
    if (!searchText || !searchText.trim()) {
      return false;
    }
    
    // Check ID match (exact or partial)
    const idStr = String(item.id || '');
    return idStr.includes(searchText.trim());
  }, []);

  // Helper function to recursively check if an epic contains any matching items
  const epicContainsMatch = useCallback((epic, searchText) => {
    if (!searchText || !searchText.trim()) {
      return false;
    }
    
    // Check epic itself
    if (matchesSearch(epic, searchText)) {
      return true;
    }
    
    // Check features
    if (epic.features && epic.features.length > 0) {
      for (const feature of epic.features) {
        if (matchesSearch(feature, searchText)) {
          return true;
        }
        
        // Check backlog items
        if (feature.items && feature.items.length > 0) {
          for (const backlogItem of feature.items) {
            if (matchesSearch(backlogItem, searchText)) {
              return true;
            }
          }
        }
      }
    }
    
    return false;
  }, [matchesSearch]);

  // Helper function to collect all matched item IDs in an epic
  const collectMatchedIds = useCallback((epic, searchText, matchedIds) => {
    if (!searchText || !searchText.trim()) {
      return;
    }
    
    // Check epic itself
    if (matchesSearch(epic, searchText)) {
      matchedIds.add(epic.id);
    }
    
    // Check features
    if (epic.features && epic.features.length > 0) {
      for (const feature of epic.features) {
        if (matchesSearch(feature, searchText)) {
          matchedIds.add(feature.id);
        }
        
        // Check backlog items
        if (feature.items && feature.items.length > 0) {
          for (const backlogItem of feature.items) {
            if (matchesSearch(backlogItem, searchText)) {
              matchedIds.add(backlogItem.id);
            }
          }
        }
      }
    }
  }, [matchesSearch]);
  
  // Filter work items based on selected quarter and filters
  const applyFilters = useCallback((workItemsData, filterValues, selectedQuarter) => {
    if (!workItemsData || workItemsData.length === 0) {
      return workItemsData;
    }
    
    let filtered = workItemsData;
    
    // Apply search filter first (if search text is provided)
    if (filterValues.searchText && filterValues.searchText.trim()) {
      const searchText = filterValues.searchText.trim();
      const matchedIds = new Set();
      
      // Filter epics that contain matches
      filtered = workItemsData.filter(epic => {
        const containsMatch = epicContainsMatch(epic, searchText);
        if (containsMatch) {
          collectMatchedIds(epic, searchText, matchedIds);
        }
        return containsMatch;
      });
      
      // Store matched IDs for auto-expansion
      setMatchedItemIds(matchedIds);
    } else {
      // Clear matched IDs when search is cleared
      setMatchedItemIds(new Set());
    }
    
    // Apply other filters
    filtered = filtered.filter(epic => {
      // Filter by quarter (skip if search is active to show all matching epics)
      if (filterValues.searchText && filterValues.searchText.trim()) {
        // When searching, don't filter by quarter
      } else if (selectedQuarter) {
        // To be Planned: epics with no target date (same epic can also appear in Q1/Q2 tabs with dated children)
        if (selectedQuarter.label === 'To be Planned') {
          if (epic.targetDate) return false;
        } else {
          // An epic should appear in a quarter if:
          // 1. The epic's target date is in that quarter, OR
          // 2. Any of its features' (only features, not PBIs) target dates are in that quarter
          // This means an epic can appear in multiple quarters (its own quarter AND quarters where features are)
          
          let epicInQuarter = false;
          
          // Check if epic's target date is in the quarter
          if (epic.targetDate) {
            const epicDate = new Date(epic.targetDate);
            const quarterStart = selectedQuarter.startDate;
            const quarterEnd = selectedQuarter.endDate;
            
            if (epicDate >= quarterStart && epicDate <= quarterEnd) {
              epicInQuarter = true;
            }
          }
          
          // Check if any features (only features, not PBIs) are in the quarter
          // This check is independent - epic can appear in both its own quarter AND features' quarters
          // Note: Items in epic.features should be features (backend ensures this)
          if (epic.features && epic.features.length > 0) {
            const hasFeatureInQuarter = epic.features.some(feature => {
              // Only check features - exclude PBIs, Tasks, Bugs
              // If workItemType is not set, assume it's a feature since it's in epic.features
              const isFeature = !feature.workItemType || 
                               feature.workItemType === 'Feature' || 
                               feature.level === 'Feature';
              const isNotPBIOrTask = feature.workItemType !== 'Product Backlog Item' && 
                                     feature.workItemType !== 'Task' && 
                                     feature.workItemType !== 'PBI' &&
                                     feature.workItemType !== 'Bug';
              
              if (!isFeature && !isNotPBIOrTask) return false;
              if (!feature.targetDate) return false;
              
              const featureDate = new Date(feature.targetDate);
              // Normalize dates to start of day for comparison (UTC)
              const featureDateNormalized = new Date(Date.UTC(featureDate.getFullYear(), featureDate.getMonth(), featureDate.getDate()));
              const quarterStart = selectedQuarter.startDate;
              const quarterEnd = selectedQuarter.endDate;
              
              // Quarter dates are already in UTC from the quarters array
              const isInRange = featureDateNormalized >= quarterStart && featureDateNormalized <= quarterEnd;
              
              // Debug logging for epic 884141
              if (epic.id === 884141 || epic.id === '884141') {
                console.log('Epic 884141 feature check:', {
                  featureId: feature.id,
                  featureTitle: feature.title,
                  featureWorkItemType: feature.workItemType,
                  featureLevel: feature.level,
                  isFeature,
                  isNotPBIOrTask,
                  featureTargetDate: feature.targetDate,
                  featureDateNormalized: featureDateNormalized.toISOString(),
                  quarterStart: quarterStart.toISOString(),
                  quarterEnd: quarterEnd.toISOString(),
                  isInRange,
                  selectedQuarter: selectedQuarter.label
                });
              }
              
              return isInRange;
            });
            
            if (hasFeatureInQuarter) {
              epicInQuarter = true;
              // Debug logging
              if (epic.id === 884141 || epic.id === '884141') {
                console.log('Epic 884141 will appear in quarter:', selectedQuarter.label, 'because it has features in this quarter');
              }
            } else if (epic.id === 884141 || epic.id === '884141') {
              console.log('Epic 884141 will NOT appear in quarter:', selectedQuarter.label, '- no features found in this quarter');
            }
          } else if (epic.id === 884141 || epic.id === '884141') {
            console.log('Epic 884141 has no features array or empty features');
          }
          
          if (!epicInQuarter) {
            return false;
          }
        }
      }
      
      // Filter by epic target date (legacy filter) - skip when "To be Planned" is selected so items with no date are shown
      if (filterValues.epicTargetDate && selectedQuarter?.label !== 'To be Planned') {
        const epicTargetDateFormatted = epic.targetDate ? formatDate(epic.targetDate) : null;
        const targetDateFilters = filterValues.epicTargetDate.split(',');
        if (!epicTargetDateFormatted || !targetDateFilters.includes(epicTargetDateFormatted)) {
          return false;
        }
      }
      
      // Filter by team (epic level or feature level)
      if (filterValues.team) {
        const teamFilters = filterValues.team.split(',');
        const epicTeam = epic.team || '';
        const hasMatchingEpicTeam = teamFilters.includes(epicTeam);
        
        // Check if any feature has a matching team
        const hasMatchingFeatureTeam = epic.features && epic.features.some(feature => 
          teamFilters.includes(feature.team || '')
        );
        
        if (!hasMatchingEpicTeam && !hasMatchingFeatureTeam) {
          return false;
        }
      }
      
      // Filter by state
      if (filterValues.state) {
        const stateFilters = filterValues.state.split(',');
        const epicState = epic.state || '';
        if (!epicState || !stateFilters.includes(epicState)) {
          return false;
        }
      }
      
      return true;
    });
    
    return filtered;
  }, [epicContainsMatch, collectMatchedIds]);
  
  // Apply filters when work items, filters, or active tab change (use stream-filtered list when in stream mode)
  useEffect(() => {
    const selectedQuarter = getSelectedQuarter(activeTab);
    let filtered = applyFilters(workItemsByStream, filters, selectedQuarter);
    
    // When an epic is included (quarter/epic filter passed), show ALL its features so the tree matches the DB
    if (selectedQuarter && selectedQuarter.label !== 'To be Planned') {
      filtered = filtered.map(epic => ({
        ...epic,
        features: epic.features || []
      }));
    }
    
    setFilteredWorkItems(filtered);
  }, [workItemsByStream, filters, activeTab, applyFilters]);
  
  useEffect(() => {
    const fetchWorkItems = async () => {
      try {
        setLoading(true);
        // Use the API service to get the hierarchy data (public endpoint)
        const response = await get('/api/workitems/from-epic-hierarchies');
        
        // Ensure data is an array
        const workItemsArray = Array.isArray(response.data) ? response.data : 
                              (response && Array.isArray(response.data) ? response.data : []);
        
        console.log('Fetched work items:', workItemsArray);
        console.log('Work items count:', workItemsArray.length);
        console.log('Sample work item:', workItemsArray[0]);
        setWorkItems(workItemsArray);
        setError(null);
      } catch (err) {
        console.error('Error fetching work items:', err);
        setError('Failed to load work items. Please try again later.');
        setWorkItems([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchWorkItems();
  }, []);

  const fetchPlannedIds = useCallback(async () => {
    try {
      const res = await getPlanningItems();
      const list = res?.data ?? res ?? [];
      const inPlanning = new Set(
        (Array.isArray(list) ? list : [])
          .filter((i) => i.quarter != null && String(i.quarter).trim() !== '')
          .map((i) => String(i.adoId).trim())
      );
      setPlannedItemIds(inPlanning);
    } catch (err) {
      console.error('Error fetching planning items for highlight:', err);
      setPlannedItemIds(new Set());
    }
  }, []);

  useEffect(() => {
    fetchPlannedIds();
  }, [fetchPlannedIds]);

  // Fetch work items without parents (for bottom sections)
  useEffect(() => {
    const fetchOrphanItems = async () => {
      try {
        const response = await get('/api/workitems/without-parents');
        const data = response?.data ?? response;
        const list = Array.isArray(data) ? data : [];
        setOrphanItems(list);
      } catch (err) {
        console.error('Error fetching items without parents:', err);
        setOrphanItems([]);
      }
    };
    fetchOrphanItems();
  }, []);

  // Filter orphan items by selected quarter and split into three categories
  const orphanByQuarterAndCategory = useMemo(() => {
    const selectedQuarter = getSelectedQuarter(activeTab);
    if (!selectedQuarter) {
      return { features: [], pbiBugs: [], others: [] };
    }

    const inQuarter = (item) => {
      const targetDate = item.targetDate ? new Date(item.targetDate) : null;
      if (selectedQuarter.label === 'To be Planned') {
        return !targetDate;
      }
      if (!targetDate) return false;
      return targetDate >= selectedQuarter.startDate && targetDate <= selectedQuarter.endDate;
    };

    const quarterFiltered = orphanItems.filter(inQuarter);
    const features = quarterFiltered.filter((item) => item.workItemType === 'Feature');
    const pbiBugs = quarterFiltered.filter(
      (item) =>
        item.workItemType === 'Product Backlog Item' || item.workItemType === 'Bug'
    );
    const others = quarterFiltered.filter(
      (item) =>
        item.workItemType !== 'Feature' &&
        item.workItemType !== 'Product Backlog Item' &&
        item.workItemType !== 'Bug' &&
        item.workItemType !== 'Epic'
    );

    return { features, pbiBugs, others };
  }, [orphanItems, activeTab]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Move work item (and all descendants) to Planned tab under the given quarter. Does not remove from Work Items.
  const handleMoveToPlanning = useCallback(async (adoId, planId) => {
    setError(null);
    try {
      const res = await setPlanningQuarter(adoId, planId);
      // API returns { success, data: { updatedIds, ... }, message }; authPatch returns that body
      const payload = res?.data ?? res;
      const updatedIds = payload?.updatedIds ?? payload?.data?.updatedIds;
      if (updatedIds && updatedIds.length > 0) {
        try {
          await authPost('/api/planning/refresh', { adoIds: updatedIds });
        } catch (refreshErr) {
          console.warn('Planning refresh after move failed (move succeeded):', refreshErr);
        }
      }
      await fetchPlannedIds(); // Update row highlight so moved item shows as planned
    } catch (err) {
      console.error('Move to Planning failed:', err);
      const message = err?.response?.data?.message ?? err?.message ?? 'Move to Planning failed. Try again or check you are logged in with the right role.';
      setError(message);
      throw err;
    }
  }, [fetchPlannedIds]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await get('/api/workitems/from-epic-hierarchies');
      const workItemsArray = Array.isArray(response.data) ? response.data : (response?.data ?? []);
      setWorkItems(workItemsArray);
      await fetchPlannedIds();
    } catch (err) {
      console.error('Error refreshing work items:', err);
    } finally {
      setRefreshing(false);
    }
  }, [fetchPlannedIds]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Work Items
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View and filter work items in a hierarchical structure
        </Typography>
      </Box>
      
      <WorkItemsFilterPanel 
        onFilterChange={handleFilterChange}
        workItems={workItems}
        initialSearchText={filters.searchText}
      />

      {/* Top-level tabs: Work Items | Orphaned Items */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 2 }}>
        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
          <Tab label="Work Items" id="work-items-tab" aria-controls="work-items-panel" />
          <Tab label="Orphaned Items" id="orphaned-items-tab" aria-controls="orphaned-items-panel" />
        </Tabs>
      </Box>
      
      {/* Work Items tab content */}
      {mainTab === 0 && (
        <>
          {loading ? (
            <Loading message="Loading work items..." />
          ) : error ? (
            <Paper elevation={2} style={{ padding: 16, marginTop: 16 }}>
              <Typography color="error">{error}</Typography>
            </Paper>
          ) : workItemsByStream.length === 0 ? (
            <Paper elevation={2} style={{ padding: 16, marginTop: 16 }}>
              <Typography>
                {streamName
                  ? `No work items in this stream.${streamName === 'UnMapped' ? ' (Teams without a stream assignment)' : ''}`
                  : 'No work items found matching the selected filters.'}
              </Typography>
            </Paper>
          ) : (
            <Box mt={2} sx={{ width: '100%', maxWidth: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                  {filteredWorkItems.length} {filteredWorkItems.length === 1 ? 'Epic' : 'Epics'} found
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  size="small"
                >
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Box>
              
              {quarters.length > 0 && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {quarters.map((quarter) => {
                      const quarterCount = workItemsByStream.filter(epic => {
                        if (quarter.label === 'To be Planned') {
                          return !epic.targetDate;
                        }
                        if (epic.targetDate) {
                          const epicDate = new Date(epic.targetDate);
                          if (epicDate >= quarter.startDate && epicDate <= quarter.endDate) {
                            return true;
                          }
                        }
                        if (epic.features && epic.features.length > 0) {
                          return epic.features.some(feature => {
                            if (!feature.targetDate) return false;
                            const featureDate = new Date(feature.targetDate);
                            return featureDate >= quarter.startDate && featureDate <= quarter.endDate;
                          });
                        }
                        return false;
                      }).length;
                      return (
                        <Tab 
                          key={quarter.label} 
                          label={`${quarter.label}${quarterCount > 0 ? ` (${quarterCount})` : ''}`}
                          disabled={quarterCount === 0}
                        />
                      );
                    })}
                  </Tabs>
                </Box>
              )}
              
              <WorkItemsTreeView 
                workItems={filteredWorkItems} 
                activeQuarter={getSelectedQuarter(activeTab)}
                matchedItemIds={matchedItemIds}
                quarters={quarters}
                onMoveToPlanning={handleMoveToPlanning}
                plannedItemIds={plannedItemIds}
              />
            </Box>
          )}
        </>
      )}

      {/* Orphaned Items tab content */}
      {mainTab === 1 && (
        <Box sx={{ width: '100%', maxWidth: '100%' }} role="tabpanel" id="orphaned-items-panel" aria-labelledby="orphaned-items-tab">
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            For quarter: {getSelectedQuarter(activeTab)?.label ?? '—'}
          </Typography>
          {quarters.length > 0 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {quarters.map((quarter) => (
                  <Tab key={quarter.label} label={quarter.label} />
                ))}
              </Tabs>
            </Box>
          )}

          <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' }, '&:not(:last-child)': { borderBottom: 0 } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Features with No Parents ({orphanByQuarterAndCategory.features.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
              <OrphanItemsTable items={orphanByQuarterAndCategory.features} />
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' }, '&:not(:last-child)': { borderBottom: 0 } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                PBI / Bugs with No Parents ({orphanByQuarterAndCategory.pbiBugs.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
              <OrphanItemsTable items={orphanByQuarterAndCategory.pbiBugs} />
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Others with no Parent ({orphanByQuarterAndCategory.others.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
              <OrphanItemsTable items={orphanByQuarterAndCategory.others} />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default WorkItemsView;
